import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchRealtyCities } from '@eliadmin/core/src/api/realty/realty-api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useRealtyStore } from '../../../../stores/realty-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useId } from 'react';
import { RealtyFilterChevron } from '../realty-filter-chevron';
import {
  KOSICEREAL_REALTY_FILTER_RECIPE,
  type RealtyFilterTemplateSetting,
  type RealtyFilterVariant,
} from '../realty-filter-recipe';
import styles from '../realty-filter.module.css';

type RealtyFilterLocationProps = {
  templateSetting?: RealtyFilterTemplateSetting;
  variant?: RealtyFilterVariant;
};

export const RealtyFilterLocation = ({
  templateSetting,
  variant = 'desktop',
}: RealtyFilterLocationProps) => {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { filter, setFilter } = useRealtyStore();
  const instanceId = useId();
  const headingId = `${instanceId}-realty-filter-location-heading`;
  const statusId = `${instanceId}-realty-filter-location-status`;

  const {
    data: cityOptions,
    isError,
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: [CacheKey.REALTY_CITIES, locale],
    queryFn: () => fetchRealtyCities({ locale }),
    enabled: templateSetting?.enabled !== false,
  });

  const semanticPlaceholderKey = KOSICEREAL_REALTY_FILTER_RECIPE.locationPlaceholderTranslationKey;
  const semanticPlaceholder = t(semanticPlaceholderKey);
  const placeholder =
    semanticPlaceholder === semanticPlaceholderKey ? t('text1965') : semanticPlaceholder;
  const heading = t('text1329');
  const isSuccessfulEmpty = isSuccess && cityOptions.data.length === 0;
  const isCitySelectionDisabled = isLoading || isError || isSuccessfulEmpty;
  if (templateSetting && !templateSetting.enabled) {
    return null;
  }

  return (
    <div className={styles.fieldGroup} style={{ order: templateSetting?.order }}>
      <section
        className={styles.section}
        aria-busy={isLoading || undefined}
        aria-describedby={isLoading || isError ? statusId : undefined}
        aria-labelledby={headingId}
        data-query-state={isLoading ? 'loading' : isError ? 'error' : 'ready'}
      >
        <h2 id={headingId} className={styles.heading}>
          {heading}
        </h2>
        {(isLoading || isError) && (
          <p id={statusId} className={styles.srOnly} role={isError ? 'alert' : 'status'}>
            {t(isError ? 'text530' : 'text439')}
          </p>
        )}
        <Select
          value={filter.city}
          onValueChange={value => {
            setFilter({ city: value });
          }}
        >
          <SelectTrigger
            variant="outlined"
            className={styles.selectTrigger}
            innerTriggerClassName={styles.selectTriggerInner}
            aria-label={heading}
            aria-describedby={isLoading || isError ? statusId : undefined}
            disabled={isCitySelectionDisabled}
            isLoading={isLoading}
            expandIcon={
              <RealtyFilterChevron
                className={styles.selectChevron}
                data-eli-part="listing-location-chevron"
              />
            }
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className={styles.selectContent}>
            {cityOptions?.data?.map(city => (
              <SelectItem
                key={city.id}
                value={city.id.toString()}
                className={cn(
                  styles.selectItem,
                  variant === 'mobile' && styles.selectItemMobile,
                  'ea-realty-filter__location-item',
                )}
              >
                {city.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </section>
      <Separator className={styles.separator} />
    </div>
  );
};
